'use client'

import { useAtom } from 'jotai'
import { useLocale } from 'next-intl'
import { useMemo } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useInstrumentMap } from '@/features/order/order-form/hooks/use-instrument-map'
import { useOrderFormTradeVenue } from '@/features/order/order-form/hooks/use-order-form-trade-venue'
import { resolveQuote } from '@/features/order/order-form/lib/instrument-filter'
import { useUnifiedOrderFormContext } from '@/features/order/order-form/lib/order-form-context'
import { parseSymbolExpiry } from '@/features/order/order-form/lib/parse-instrument-symbol'
import {
  buildOptionInstrument,
  formatExpiryDate,
  getAvailableExpiries,
  getAvailableStrikes,
  resolveOptionInstrument,
} from '@/features/order/order-form/lib/parse-option-instrument'
import { useMultiExchangeInstrumentMap } from '@/hooks/use-multi-exchange-instrument-map'
import { parseExchangeField } from '@/lib/proto/util'
import { getInstrumentMapForExchange } from '@/lib/utils/exchange-instrument-map'
import { useAccountPrimaryExchange } from '@/service/account-exchanges.hook'
import { orderFormSelectionAtomFamily } from '@/state/order'
import { SelectedOption } from '@/types/dashboard'
import { InstrumentKind } from '@/types/generated/common/types'

type ExpiryDropdownProps =
  | {
      mode?: 'option'
      option: SelectedOption
      index: number
      lastPrice?: string
      base?: never
      quote?: never
      symbol?: never
      onSymbolChange?: never
    }
  | {
      mode: 'future'
      base: string
      quote: string
      symbol: string
      onSymbolChange?: (symbol: string) => void
      option?: never
      index?: never
    }

export default function ExpiryDropdown(props: ExpiryDropdownProps) {
  if (props.mode === 'future') {
    return <FutureExpiry {...props} />
  }
  return <OptionExpiry option={props.option} index={props.index} lastPrice={props.lastPrice} />
}

/** Option expiry — uses selectedOptions atom */
function OptionExpiry({
  option,
  index,
  lastPrice,
}: {
  option: SelectedOption
  index: number
  lastPrice?: string
}) {
  const locale = useLocale()
  const formContext = useUnifiedOrderFormContext()
  const [, setSelectionAtom] = useAtom(orderFormSelectionAtomFamily(formContext.cardId))
  const setSelectedOptions = (
    update: SelectedOption[] | ((prev: SelectedOption[]) => SelectedOption[])
  ) =>
    setSelectionAtom((prev) => ({
      ...prev,
      selectedOptions: typeof update === 'function' ? update(prev.selectedOptions) : update,
    }))
  const cardTradeVenue = useOrderFormTradeVenue(formContext.cardId)
  const primaryExchange = useAccountPrimaryExchange(cardTradeVenue)
  // Resolve against the option's OWN exchange, not the card's primary one —
  // positions (and thus shift-clicked options) can come from any connected
  // account/exchange, and scoping to primaryExchange alone silently misses
  // the instrument (empty Strike/Expiry) whenever they differ.
  const multiExchangeInstrumentMap = useMultiExchangeInstrumentMap()
  const optionExchange = parseExchangeField(option.exchange, primaryExchange)
  const instrumentMap = useMemo(
    () => getInstrumentMapForExchange(multiExchangeInstrumentMap, optionExchange),
    [multiExchangeInstrumentMap, optionExchange]
  )
  const instruments = useMemo(() => Array.from(instrumentMap.values()), [instrumentMap])

  const parsed = resolveOptionInstrument(
    option.instrumentName,
    instrumentMap.get(option.instrumentName)
  )
  if (!parsed) return null

  const { ticker, expiry: currentExpiry, strike, type } = parsed
  const availableExpiries = getAvailableExpiries(instruments, ticker)

  const handleExpiryChange = (newExpiry: string) => {
    if (newExpiry === currentExpiry) return

    const strikes = getAvailableStrikes(instruments, ticker, newExpiry)
    const findInstrument = (targetStrike: number) =>
      instruments.find((inst) => {
        const p = resolveOptionInstrument(inst.symbol, inst)
        return (
          p &&
          p.ticker === ticker &&
          p.expiry === newExpiry &&
          p.strike === targetStrike &&
          p.type === type
        )
      })

    // Preserve the selected strike whenever the same call/put contract exists
    // on the new expiry. Only fall back to ATM when that strike is unavailable.
    let nextStrike = strike
    let matchingInstrument = findInstrument(strike)

    if (!matchingInstrument) {
      const indexPrice = parseFloat(lastPrice ?? '') || 0
      nextStrike =
        strikes.length === 0
          ? strike
          : indexPrice > 0
            ? strikes.reduce((current, candidate) =>
                Math.abs(candidate - indexPrice) < Math.abs(current - indexPrice)
                  ? candidate
                  : current
              )
            : strikes[Math.floor(strikes.length / 2)]
      matchingInstrument = findInstrument(nextStrike)
    }

    const newInstrumentName =
      matchingInstrument?.symbol || buildOptionInstrument(ticker, newExpiry, nextStrike, type)
    setSelectedOptions((prev) =>
      prev.map((opt, i) =>
        i === index ? { ...opt, instrumentName: newInstrumentName, price: '-' } : opt
      )
    )
  }

  return (
    <Select value={currentExpiry} onValueChange={handleExpiryChange}>
      <SelectTrigger
        hideIcon
        data-1p-ignore
        tabIndex={-1}
        className="font-ibm-plex-sans z-40! h-auto w-fit cursor-pointer gap-1 border-none bg-transparent p-0 text-sm text-white/50 hover:text-white"
      >
        <SelectValue className={'whitespace-pre'}>
          {formatExpiryDate(currentExpiry, locale)}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="z-[60]">
        {availableExpiries.map((exp) => (
          <SelectItem key={exp} value={exp} className="text-xs">
            {formatExpiryDate(exp, locale)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/** Future expiry — uses onSymbolChange callback */
function FutureExpiry({
  base,
  quote,
  symbol,
  onSymbolChange,
}: {
  base: string
  quote: string
  symbol: string
  onSymbolChange?: (symbol: string) => void
}) {
  const locale = useLocale()
  const formContext = useUnifiedOrderFormContext()
  const cardTradeVenue = useOrderFormTradeVenue(formContext.cardId)
  const primaryExchange = useAccountPrimaryExchange(cardTradeVenue)
  const instrumentMap = useInstrumentMap(primaryExchange)
  const instruments = useMemo(() => Array.from(instrumentMap.values()), [instrumentMap])

  const futureInstruments = instruments.filter(
    (inst) =>
      inst.baseCurrency?.toUpperCase() === base.toUpperCase() &&
      resolveQuote(inst) === quote.toUpperCase() &&
      inst.kind === InstrumentKind.INSTRUMENT_KIND_FUTURE &&
      !inst.symbol.toLowerCase().includes('-perp')
  )

  const currentExpiry = parseSymbolExpiry(symbol) ?? ''

  if (futureInstruments.length <= 1) {
    return (
      <span className="font-ibm-plex-sans text-xs text-white/40">
        {formatExpiryDate(currentExpiry, locale)}
      </span>
    )
  }

  const toMs = (sym: string) => {
    const exp = parseSymbolExpiry(sym)
    if (!exp) return 0
    const dayLen = /^\d{2}[A-Z]/i.test(exp) ? 2 : 1
    const day = parseInt(exp.slice(0, dayLen), 10)
    const month = [
      'JAN',
      'FEB',
      'MAR',
      'APR',
      'MAY',
      'JUN',
      'JUL',
      'AUG',
      'SEP',
      'OCT',
      'NOV',
      'DEC',
    ].indexOf(exp.slice(dayLen, dayLen + 3).toUpperCase())
    const year = 2000 + parseInt(exp.slice(dayLen + 3), 10)
    return new Date(year, month, day).getTime()
  }

  const sorted = [...futureInstruments].sort((a, b) => toMs(a.symbol) - toMs(b.symbol))

  return (
    <Select value={symbol} onValueChange={(sym) => onSymbolChange?.(sym)}>
      <SelectTrigger
        hideIcon
        data-1p-ignore
        tabIndex={-1}
        className="font-ibm-plex-sans z-40! h-auto w-fit gap-1 border-none bg-transparent p-0 text-xs text-white/40 hover:text-white"
      >
        <SelectValue>{formatExpiryDate(currentExpiry, locale)}</SelectValue>
      </SelectTrigger>
      <SelectContent className="z-[60]">
        {sorted.map((inst) => {
          const exp = parseSymbolExpiry(inst.symbol) ?? inst.symbol
          return (
            <SelectItem key={inst.symbol} value={inst.symbol} className="text-xs">
              {formatExpiryDate(exp, locale)}
            </SelectItem>
          )
        })}
      </SelectContent>
    </Select>
  )
}
